import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { useSettings, logoUrl } from "../context/SettingsContext";
import Brand from "../components/Brand";
import LanguageSwitch from "../components/LanguageSwitch";
import { formatApiErrorDetail } from "../lib/api";
import { ForgotPasswordDialog } from "../components/ForgotPasswordDialog";
import { RegisterDialog } from "../components/RegisterDialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Phone, Lock, Loader2 } from "lucide-react";

const DEFAULT_BG = "https://images.pexels.com/photos/18145430/pexels-photo-18145430.jpeg";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const { settings } = useSettings();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(phone, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const imgPos = settings.welcome_image_position || "left";
  const textPos = settings.welcome_text_position || "center";
  const textAlign = settings.welcome_text_align || "start";
  const welcomeImg = logoUrl(settings.welcome_image_path) || DEFAULT_BG;
  const welcomeTitle = settings.welcome_title || t("login.title");
  const welcomeSubtitle = settings.welcome_subtitle || t("login.subtitle");
  const showImage = imgPos !== "none";

  const vJustify = textPos === "top" ? "justify-start" : textPos === "bottom" ? "justify-end" : "justify-center";
  const align = textAlign === "center" ? "items-center text-center" : textAlign === "end" ? "items-end text-end" : "items-start text-start";

  const imagePanel = (
    <div className="relative hidden lg:block" data-testid="welcome-panel">
      <img src={welcomeImg} alt="welcome" className="absolute inset-0 h-full w-full object-cover" data-testid="welcome-image" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(79,70,229,0.85), rgba(15,23,42,0.75))" }} />
      <div className={`relative z-10 flex h-full flex-col ${vJustify} p-12 text-white`}>
        <div className={`flex flex-col ${align} gap-3`}>
          <Brand />
          <h1 className="font-heading text-4xl font-bold leading-tight" data-testid="welcome-title">{welcomeTitle}</h1>
          <p className="text-lg text-white/80" data-testid="welcome-subtitle">{welcomeSubtitle}</p>
        </div>
      </div>
    </div>
  );

  const formPanel = (
    <div className="relative flex flex-col items-center justify-center bg-background px-6 py-12">
      <div className="absolute top-5 end-5">
        <LanguageSwitch />
      </div>
      <div className="w-full max-w-sm">
        <div className="lg:hidden mb-8 flex justify-center">
          <Brand size="lg" />
        </div>
        <h2 className="font-heading text-2xl font-semibold text-foreground">{welcomeTitle}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{welcomeSubtitle}</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="phone">{t("login.phone")}</Label>
            <div className="relative">
              <Phone className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="phone" data-testid="login-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("login.phonePh")} className="ps-9" dir="ltr" required />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("login.password")}</Label>
            <div className="relative">
              <Lock className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("login.passwordPh")} className="ps-9" required />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
          )}

          <button type="submit" disabled={loading} data-testid="login-submit"
            className="brand-btn flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-medium transition-opacity hover:opacity-90 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? t("login.signing") : t("login.submit")}
          </button>
        </form>

        <button type="button" onClick={() => setForgotOpen(true)} data-testid="forgot-password-link"
          className="mt-3 w-full text-center text-sm text-muted-foreground hover:text-foreground hover:underline">
          {t("forgot.link")}
        </button>

        <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" />
          {t("login.or")}
          <div className="h-px flex-1 bg-border" />
        </div>

        <button type="button" onClick={googleSignIn} data-testid="google-signin-btn"
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary">
          <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
          </svg>
          {t("login.google")}
        </button>

        <button type="button" onClick={() => setRegisterOpen(true)} data-testid="register-link"
          className="mt-4 w-full text-center text-sm font-medium brand-text hover:underline">
          {t("register.link")}
        </button>
      </div>
    </div>
  );

  if (!showImage) {
    return (
      <div className="min-h-screen" data-testid="login-layout-none">
        {formPanel}
        <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
        <RegisterDialog open={registerOpen} onOpenChange={setRegisterOpen} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen grid lg:grid-cols-2`} data-testid={`login-layout-${imgPos}`}>
      {imgPos === "right" ? (
        <>
          <div className="lg:order-1">{formPanel}</div>
          <div className="lg:order-2">{imagePanel}</div>
        </>
      ) : (
        <>
          {imagePanel}
          {formPanel}
        </>
      )}
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
      <RegisterDialog open={registerOpen} onOpenChange={setRegisterOpen} />
    </div>
  );
}
